import React, { useMemo } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Collapse,
} from "@mui/material";
import { Delete as DeleteIcon, AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { doc, updateDoc } from "firebase/firestore";
import Tooltip from "@mui/material/Tooltip";
import {
  useNetworkData,
  friendlyLabel,
  findElementByName,
  getAnswerVals,
  getGroupedAnswerVals,
  SurveyElement,
} from "../hooks/useNetworkData";
import { auth, db } from "../firebase";
import { findNetworkQuestionGroup } from "../utils/networkQuestionGroups";

function formatTimestamp(value?: string | Date) {
  if (!value) return null;
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString();
  } catch {
    return null;
  }
}

const ValueChips = ({ values }: { values: string[] }) => {
  if (!values.length) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {values.map((val, idx) => (
            <Chip
              key={`${val}-${idx}`}
              label={val}
              size="small"
              sx={{ mb: 0.5, borderRadius: 1, backgroundColor: "#eef2f5" }}
            />
          ))}
    </Stack>
  );
};

const sectionTitle = (text: string) => (
  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
    {text}
  </Typography>
);

const cleanFragment = (val: string) =>
  val
    .replace(/^[\s,;|/&+-]+/, "")
    .replace(/[\s,;|/&+-]+$/, "")
    .replace(/\s+/g, " ")
    .trim();

const expandParenthetical = (value: string): string[] => {
  const match = value.match(/^(.*)\(([^)]+)\)$/);
  if (!match) return [cleanFragment(value)];
  const base = cleanFragment(match[1]);
  const inner = match[2];
  const innerParts = inner
    .split(/[,/&]|(?:\s+\band\b\s+)/i)
    .map((p) => cleanFragment(p))
    .filter(Boolean);
  if (innerParts.length > 1 && base) {
    return innerParts.map((p) => cleanFragment(`${base} (${p})`));
  }
  return [cleanFragment(value)];
};

const splitOnConjunction = (value: string): string[] => {
  const parts = value.split(/\s+(?:and|&)\s+/i).map((p) => cleanFragment(p)).filter(Boolean);
  if (parts.length <= 1) return [cleanFragment(value)];

  const looksLikeOrg = (p: string) =>
    /^[A-Z0-9]/.test(p) ||
    /\b(association|council|school|college|university|trust|committee|team|department|dept|foundation|group|clinic|hospital|church|club|network|agency)\b/i.test(
      p
    );

  const confidentSplit = parts.length > 1 && parts.filter((p) => looksLikeOrg(p)).length >= 2;
  return confidentSplit ? parts : [cleanFragment(value)];
};

const splitEntities = (raw: string): string[] => {
  const normalized = (raw || "").replace(/\s+/g, " ").trim();
  if (!normalized) return [];

  const primaryParts = normalized
    .split(/[\n;]+/)
    .flatMap((p) => p.split(/\s*\/\s*/))
    .map((p) => p.trim())
    .filter(Boolean);

  const collected: string[] = [];

  primaryParts.forEach((part) => {
    const basePieces = part
      .split(/,(?![^()]*\))/) // commas not inside parens
      .map((p) => cleanFragment(p))
      .filter(Boolean);

    basePieces.forEach((piece) => {
      expandParenthetical(piece).forEach((expanded) => {
        splitOnConjunction(expanded).forEach((finalPart) => {
          if (finalPart) collected.push(finalPart);
        });
      });
    });
  });

  const uniq: string[] = [];
  collected.forEach((p) => {
    const lc = p.toLowerCase();
    if (!uniq.some((u) => u.toLowerCase() === lc)) {
      uniq.push(p);
    }
  });

  return uniq;
};

const looksLikeMultiEntity = (raw: string): boolean => splitEntities(raw).length > 1;

type SplitSuggestion = {
  orgField: string;
  base: Record<string, any>;
  original: Record<string, any>;
  names: string[];
  raw: string;
};

const getOrgComment = (entry: any, orgField?: string) => {
  if (!orgField) return "";
  const a = entry?.[`${orgField}-Comment`];
  const b = entry?.[`${orgField}Comment`];
  return typeof a === "string" ? a : typeof b === "string" ? b : "";
};

const buildSplitSuggestion = (entry: any, orgField?: string): SplitSuggestion | null => {
  if (!orgField) return null;
  const rawComment = getOrgComment(entry, orgField);
  const orgValRaw =
    typeof entry?.[orgField] === "string"
      ? entry[orgField]
      : Array.isArray(entry?.[orgField])
      ? (entry?.[orgField] as string[]).join(", ")
      : "";

  const rawForSplit =
    (rawComment && looksLikeMultiEntity(rawComment) && rawComment) ||
    (orgValRaw && looksLikeMultiEntity(orgValRaw) && orgValRaw) ||
    "";
  if (!rawForSplit) return null;

  const names = splitEntities(rawForSplit);
  if (names.length <= 1) return null;

  const base: Record<string, any> = { ...entry };
  delete base[orgField];
  delete base[`${orgField}-Comment`];
  delete base[`${orgField}Comment`];

  return { names, raw: rawForSplit, base, orgField, original: { ...entry } };
};

const copyText = async (value: string) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand("copy");
    return true;
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

function buildPanelFieldInfo(panel?: SurveyElement): { order: string[]; labels: Record<string, string> } {
  const order: string[] = [];
  const labels: Record<string, string> = {};
  panel?.templateElements?.forEach((el) => {
    if (!el?.name) return;
    order.push(el.name);
    labels[el.name] = el.title || friendlyLabel(el.name);
  });
  return { order, labels };
}

export default function NetworkDebug() {
  const { id: surveyId, source, target } = useParams<{ id: string; source: string; target: string }>();
  const [refreshKey, setRefreshKey] = React.useState(0);
  const { responses, surveyJson, surveyData, loading, error } = useNetworkData({ surveyId, source, target, refreshKey });
  const [splitModal, setSplitModal] = React.useState<{
    open: boolean;
    suggestion?: SplitSuggestion;
    title?: string;
    responseId?: string;
    entryIndex?: number;
  }>({ open: false });
  const [copyFeedback, setCopyFeedback] = React.useState<string | null>(null);
  const [editableNames, setEditableNames] = React.useState<string[]>([]);
  const [showJson, setShowJson] = React.useState(false);
  const [savingSplit, setSavingSplit] = React.useState(false);
  const [splitError, setSplitError] = React.useState<string | null>(null);
  const [inlineSimpleEdits, setInlineSimpleEdits] = React.useState<Record<string, Record<number, string>>>({});
  const [inlineEntryEdits, setInlineEntryEdits] = React.useState<
    Record<string, Record<number, Record<string, string[]>>>
  >({});
  const [inlineTargetEdits, setInlineTargetEdits] = React.useState<Record<string, string>>({});
  const [inlineSourceEdits, setInlineSourceEdits] = React.useState<Record<string, string[]>>({});
  const [inlineSavingKey, setInlineSavingKey] = React.useState<string | null>(null);
  const [inlineEditError, setInlineEditError] = React.useState<string | null>(null);
  const [inlineEditFeedback, setInlineEditFeedback] = React.useState<string | null>(null);
  const [simpleSplitModal, setSimpleSplitModal] = React.useState<{
    open: boolean;
    responseId?: string;
    raw?: string;
    names?: string[];
  }>({ open: false });
  const [editableSimpleNames, setEditableSimpleNames] = React.useState<string[]>([]);
  const canEditSplits = React.useMemo(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || !surveyData) return false;
    if (surveyData.userId === uid) return true;
    if (surveyData.roles && surveyData.roles[uid] === "editor") return true;
    return false;
  }, [surveyData]);

  React.useEffect(() => {
    if (splitModal.open && splitModal.suggestion) {
      setEditableNames(splitModal.suggestion.names);
      setCopyFeedback(null);
      setShowJson(false);
      setSplitError(null);
    }
  }, [splitModal]);

  React.useEffect(() => {
    if (simpleSplitModal.open && simpleSplitModal.names) {
      setEditableSimpleNames(simpleSplitModal.names);
    } else {
      setEditableSimpleNames([]);
    }
  }, [simpleSplitModal]);

  const sourceLabel = useMemo(() => {
    if (!surveyJson || !source) return friendlyLabel(source);
    const el = findElementByName(surveyJson, source);
    const group = findNetworkQuestionGroup(surveyJson, source);
    return group ? `${group.title} — all countries` : el?.title || friendlyLabel(source);
  }, [surveyJson, source]);

  const targetPanel = useMemo(() => {
    if (!surveyJson || !target) return undefined;
    return findElementByName(surveyJson, target);
  }, [surveyJson, target]);

  const sourceGroup = useMemo(() => findNetworkQuestionGroup(surveyJson, source), [surveyJson, source]);
  const targetGroup = useMemo(() => findNetworkQuestionGroup(surveyJson, target), [surveyJson, target]);

  const trimmedNames = React.useMemo(() => editableNames.map((n) => n.trim()).filter(Boolean), [editableNames]);

  const proposedPayload = React.useMemo(() => {
    if (!splitModal.suggestion) return null;
    if (trimmedNames.length === 0) {
      return splitModal.suggestion.original;
    }
    return trimmedNames.map((name) => ({
      ...splitModal.suggestion!.base,
      [splitModal.suggestion!.orgField]: name,
    }));
  }, [trimmedNames, splitModal]);

  const { order: panelFieldOrder, labels: panelFieldLabels } = useMemo(
    () => buildPanelFieldInfo(targetPanel),
    [targetPanel]
  );

  const panelOrgField = useMemo(() => {
    const orgField = panelFieldOrder.find((name) => /org|organisation|organization/i.test(name));
    return orgField || panelFieldOrder[0];
  }, [panelFieldOrder]);

  if (!surveyId || !source || !target) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Missing network route parameters.</Typography>
      </Box>
    );
  }

  if (loading && responses.length === 0) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const surveyTitle = surveyJson?.title || surveyId;

  const handleApplySplit = async () => {
    if (!surveyId || !target || !splitModal.suggestion) return;
    if (!splitModal.responseId || splitModal.entryIndex === undefined) return;
    if (!proposedPayload) {
      setSplitError("Nothing to save.");
      return;
    }

    const replacements = Array.isArray(proposedPayload) ? proposedPayload : [proposedPayload];
    const resp = responses.find((r) => r.id === splitModal.responseId);
    if (!resp) {
      setSplitError("Response not found.");
      return;
    }

    const currentPanel = Array.isArray(resp.answers?.[target]) ? [...resp.answers[target]] : [];
    if (splitModal.entryIndex < 0 || splitModal.entryIndex >= currentPanel.length) {
      setSplitError("Entry not found in response.");
      return;
    }

    const nextPanel = [...currentPanel];
    nextPanel.splice(splitModal.entryIndex, 1, ...replacements);
    const nextAnswers = { ...(resp.answers || {}), [target]: nextPanel };

    setSavingSplit(true);
    setSplitError(null);
    setCopyFeedback(null);
    try {
      await updateDoc(doc(db, "surveys", surveyId, "responses", resp.id), { answers: nextAnswers });
      setSplitModal({ open: false });
      setEditableNames([]);
      setShowJson(false);
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      setSplitError(err?.message || "Failed to save split.");
    } finally {
      setSavingSplit(false);
    }
  };

  const handleSaveSimpleEdit = async (responseId: string, idx: number) => {
    if (!surveyId || !target) return;
    const nextVal = inlineSimpleEdits[responseId]?.[idx] ?? "";
    const trimmed = nextVal.trim();
    if (!trimmed) {
      setInlineEditError("Value cannot be empty.");
      return;
    }
    const resp = responses.find((r) => r.id === responseId);
    if (!resp) {
      setInlineEditError("Response not found.");
      return;
    }
    const nominationsArray = Array.isArray(resp.answers?.[target]) ? resp.answers[target] : [];
    if (!Array.isArray(nominationsArray) || !nominationsArray.every((v) => typeof v === "string")) {
      setInlineEditError("Entry is not a simple text nomination.");
      return;
    }
    if (idx < 0 || idx >= nominationsArray.length) {
      setInlineEditError("Entry not found in response.");
      return;
    }
    const nextArray = [...nominationsArray];
    nextArray[idx] = trimmed;
    const nextAnswers = { ...(resp.answers || {}), [target]: nextArray };
    const savingKey = `${responseId}-simple-${idx}`;
    setInlineSavingKey(savingKey);
    setInlineEditError(null);
    setInlineEditFeedback(null);
    try {
      await updateDoc(doc(db, "surveys", surveyId, "responses", resp.id), { answers: nextAnswers });
      setInlineEditFeedback("Saved changes.");
      setInlineSimpleEdits((prev) => {
        const next = { ...(prev || {}) };
        if (next[responseId]) {
          const updated = { ...next[responseId] };
          delete updated[idx];
          next[responseId] = updated;
        }
        return next;
      });
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      setInlineEditError(err?.message || "Failed to save changes.");
    } finally {
      setInlineSavingKey(null);
    }
  };

  const handleSaveEntryEdit = async (responseId: string, entryIdx: number) => {
    if (!surveyId || !target) return;
    const edits = inlineEntryEdits[responseId]?.[entryIdx];
    if (!edits) {
      setInlineEditError("Nothing to save.");
      return;
    }
    const resp = responses.find((r) => r.id === responseId);
    if (!resp) {
      setInlineEditError("Response not found.");
      return;
    }
    const nominationsArray = Array.isArray(resp.answers?.[target]) ? resp.answers[target] : [];
    if (!Array.isArray(nominationsArray) || nominationsArray.length <= entryIdx) {
      setInlineEditError("Entry not found in response.");
      return;
    }
    const currentEntry = nominationsArray[entryIdx];
    if (typeof currentEntry !== "object" || currentEntry === null || Array.isArray(currentEntry)) {
      setInlineEditError("Entry is not editable in this format.");
      return;
    }
    const updatedEntry: Record<string, any> = { ...currentEntry };
    for (const [field, vals] of Object.entries(edits)) {
      const trimmed = (vals || []).map((v) => v.trim()).filter(Boolean);
      if (!trimmed.length) {
        setInlineEditError(`Field "${field}" cannot be empty.`);
        return;
      }
      const originalVal = currentEntry[field];
      updatedEntry[field] = Array.isArray(originalVal) ? trimmed : trimmed[0];
    }
    const nextPanel = [...nominationsArray];
    nextPanel[entryIdx] = updatedEntry;
    const nextAnswers = { ...(resp.answers || {}), [target]: nextPanel };
    const savingKey = `${responseId}-entry-${entryIdx}`;
    setInlineSavingKey(savingKey);
    setInlineEditError(null);
    setInlineEditFeedback(null);
    try {
      await updateDoc(doc(db, "surveys", surveyId, "responses", resp.id), { answers: nextAnswers });
      setInlineEditFeedback("Saved changes.");
      setInlineEntryEdits((prev) => {
        const next = { ...(prev || {}) };
        if (next[responseId]) {
          const updated = { ...next[responseId] };
          delete updated[entryIdx];
          next[responseId] = updated;
        }
        return next;
      });
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      setInlineEditError(err?.message || "Failed to save changes.");
    } finally {
      setInlineSavingKey(null);
    }
  };

  const handleSaveTargetStringEdit = async (responseId: string) => {
    if (!surveyId || !target) return;
    const nextVal = inlineTargetEdits[responseId] ?? "";
    const trimmed = nextVal.trim();
    if (!trimmed) {
      setInlineEditError("Value cannot be empty.");
      return;
    }
    const resp = responses.find((r) => r.id === responseId);
    if (!resp) {
      setInlineEditError("Response not found.");
      return;
    }
    const currentVal = resp.answers?.[target];
    if (typeof currentVal !== "string") {
      setInlineEditError("Entry is not editable in this format.");
      return;
    }
    const nextAnswers = { ...(resp.answers || {}), [target]: trimmed };
    const savingKey = `${responseId}-target-string`;
    setInlineSavingKey(savingKey);
    setInlineEditError(null);
    setInlineEditFeedback(null);
    try {
      await updateDoc(doc(db, "surveys", surveyId, "responses", resp.id), { answers: nextAnswers });
      setInlineEditFeedback("Saved changes.");
      setInlineTargetEdits((prev) => {
        const next = { ...(prev || {}) };
        delete next[responseId];
        return next;
      });
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      setInlineEditError(err?.message || "Failed to save changes.");
    } finally {
      setInlineSavingKey(null);
    }
  };

  const handleSaveSourceEdit = async (responseId: string) => {
    if (!surveyId || !source) return;
    const edits = inlineSourceEdits[responseId];
    if (!edits || !edits.length) {
      setInlineEditError("Nothing to save.");
      return;
    }
    const trimmed = edits.map((v) => v.trim()).filter(Boolean);
    if (!trimmed.length) {
      setInlineEditError("Value cannot be empty.");
      return;
    }
    const resp = responses.find((r) => r.id === responseId);
    if (!resp) {
      setInlineEditError("Response not found.");
      return;
    }
    const currentVal = resp.answers?.[source];
    let nextVal: any = trimmed;
    if (typeof currentVal === "string") {
      nextVal = trimmed[0];
    } else if (Array.isArray(currentVal) && currentVal.every((x) => typeof x === "string")) {
      nextVal = trimmed;
    } else {
      setInlineEditError("Source field is not editable in this format.");
      return;
    }

    const nextAnswers = { ...(resp.answers || {}), [source]: nextVal };
    const savingKey = `${responseId}-source`;
    setInlineSavingKey(savingKey);
    setInlineEditError(null);
    setInlineEditFeedback(null);
    try {
      await updateDoc(doc(db, "surveys", surveyId, "responses", resp.id), { answers: nextAnswers });
      setInlineEditFeedback("Saved changes.");
      setInlineSourceEdits((prev) => {
        const next = { ...(prev || {}) };
        delete next[responseId];
        return next;
      });
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      setInlineEditError(err?.message || "Failed to save changes.");
    } finally {
      setInlineSavingKey(null);
    }
  };

  const handleApplySimpleSplit = async () => {
    if (!surveyId || !target) return;
    if (!simpleSplitModal.responseId) return;
    const names = editableSimpleNames.map((n) => n.trim()).filter(Boolean);
    if (!names.length) {
      setSplitError("Nothing to save.");
      return;
    }
    const resp = responses.find((r) => r.id === simpleSplitModal.responseId);
    if (!resp) {
      setSplitError("Response not found.");
      return;
    }
    const nextAnswers = { ...(resp.answers || {}), [target]: names };
    setSavingSplit(true);
    setSplitError(null);
    setCopyFeedback(null);
    try {
      await updateDoc(doc(db, "surveys", surveyId, "responses", resp.id), { answers: nextAnswers });
      setSimpleSplitModal({ open: false });
      setEditableSimpleNames([]);
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      setSplitError(err?.message || "Failed to save split.");
    } finally {
      setSavingSplit(false);
    }
  };

  return (
    <Box sx={{ p: 3, pb: 6 }}>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Respondent Layout Debug
        </Typography>
        <Link component={RouterLink} to={`/network/${surveyId}/${source}/${target}`} underline="hover">
          ← Back to Network Graph
        </Link>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Survey: <strong>{surveyTitle}</strong>
      </Typography>
      {inlineEditError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {inlineEditError}
        </Typography>
      )}
      {inlineEditFeedback && (
        <Typography variant="caption" color="success.main" sx={{ display: "block", mb: 1 }}>
          {inlineEditFeedback}
        </Typography>
      )}

      <Stack spacing={3}>
        {responses.map((resp, idx) => {
          const submitted = formatTimestamp(resp.submittedAt);
          const sourceVals = sourceGroup ? getGroupedAnswerVals(resp.answers, sourceGroup) : getAnswerVals(resp.answers, source);
          const nominationsArray = Array.isArray(resp.answers?.[target]) ? resp.answers[target] : [];
          const targetValsSimple = targetGroup ? getGroupedAnswerVals(resp.answers, targetGroup) : getAnswerVals(resp.answers, target);
          const simpleArrayNoms =
            Array.isArray(nominationsArray) && nominationsArray.length > 0 && nominationsArray.every((v) => typeof v === "string");

          return (
            <Paper key={resp.id} elevation={1} sx={{ p: 2.5, borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h6">Respondent #{idx + 1}</Typography>
        {submitted && (
          <Typography variant="body2" color="text.secondary">
            Submitted: {submitted}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        {sectionTitle(sourceLabel || "Source")}
        <Stack spacing={0.75}>
          <ValueChips values={sourceVals} />
          {(() => {
            const sourceRaw = resp.answers?.[source];
            const sourceIsString = typeof sourceRaw === "string";
            const sourceIsStringArray = Array.isArray(sourceRaw) && sourceRaw.every((v: any) => typeof v === "string");
            const canEditSource = canEditSplits && (sourceIsString || sourceIsStringArray);
            const editingVals = inlineSourceEdits[resp.id];

            if (!canEditSource) return null;

            if (editingVals) {
              return (
                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                  {editingVals.map((val, i) => (
                    <Box key={`source-${i}`} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={val}
                        onChange={(e) => {
                          const nextVal = e.target.value;
                          setInlineSourceEdits((prev) => {
                            const next = { ...(prev || {}) };
                            const arr = [...(next[resp.id] || [])];
                            arr[i] = nextVal;
                            next[resp.id] = arr;
                            return next;
                          });
                          setInlineEditError(null);
                          setInlineEditFeedback(null);
                        }}
                        disabled={inlineSavingKey === `${resp.id}-source`}
                      />
                      <IconButton
                        aria-label="Remove value"
                        size="small"
                        onClick={() => {
                          setInlineSourceEdits((prev) => {
                            const next = { ...(prev || {}) };
                            const arr = [...(next[resp.id] || [])].filter((_, idx) => idx !== i);
                            next[resp.id] = arr;
                            return next;
                          });
                          setInlineEditError(null);
                          setInlineEditFeedback(null);
                        }}
                        disabled={inlineSavingKey === `${resp.id}-source`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    size="small"
                    onClick={() => {
                      setInlineSourceEdits((prev) => {
                        const next = { ...(prev || {}) };
                        const arr = [...(next[resp.id] || []), ""];
                        next[resp.id] = arr;
                        return next;
                      });
                      setInlineEditError(null);
                      setInlineEditFeedback(null);
                    }}
                    disabled={inlineSavingKey === `${resp.id}-source`}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Add value
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleSaveSourceEdit(resp.id)}
                      disabled={inlineSavingKey === `${resp.id}-source`}
                      startIcon={
                        inlineSavingKey === `${resp.id}-source` ? <CircularProgress size={14} /> : undefined
                      }
                    >
                      Save changes
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        setInlineSourceEdits((prev) => {
                          const next = { ...(prev || {}) };
                          delete next[resp.id];
                          return next;
                        })
                      }
                      disabled={inlineSavingKey === `${resp.id}-source`}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              );
            }

            return (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const initialVals: string[] = [];
                    if (sourceIsString) {
                      initialVals.push((sourceRaw as string) || "");
                    } else if (sourceIsStringArray) {
                      initialVals.push(...((sourceRaw as string[]) || []));
                    } else {
                      initialVals.push(...sourceVals);
                    }
                    setInlineSourceEdits((prev) => ({ ...(prev || {}), [resp.id]: initialVals.length ? initialVals : [""] }));
                    setInlineEditError(null);
                    setInlineEditFeedback(null);
                  }}
                  disabled={inlineSavingKey === `${resp.id}-source`}
                >
                  Edit source
                </Button>
              </Stack>
            );
          })()}
        </Stack>
      </Box>

              {sectionTitle("Nominations")}
              <Stack spacing={1.5}>
                {simpleArrayNoms ? (
                  <Stack spacing={1}>
                    {(nominationsArray as string[]).map((val, valIdx) => {
                      const names = splitEntities(val);
                      const canSplit = names.length > 1 && canEditSplits;
                      const editingVal = inlineSimpleEdits[resp.id]?.[valIdx];
                      return (
                        <Box key={`${resp.id}-simple-${valIdx}`} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                          <Stack spacing={0.75}>
                            <ValueChips values={[val]} />
                            {editingVal !== undefined ? (
                              <Stack spacing={1}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={editingVal}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setInlineSimpleEdits((prev) => ({
                                      ...(prev || {}),
                                      [resp.id]: { ...(prev?.[resp.id] || {}), [valIdx]: value },
                                    }));
                                    setInlineEditError(null);
                                    setInlineEditFeedback(null);
                                  }}
                                  disabled={inlineSavingKey === `${resp.id}-simple-${valIdx}`}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleSaveSimpleEdit(resp.id, valIdx)}
                                    disabled={!canEditSplits || inlineSavingKey === `${resp.id}-simple-${valIdx}`}
                                    startIcon={
                                      inlineSavingKey === `${resp.id}-simple-${valIdx}` ? (
                                        <CircularProgress size={14} />
                                      ) : undefined
                                    }
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={() =>
                                      setInlineSimpleEdits((prev) => {
                                        const next = { ...(prev || {}) };
                                        if (next[resp.id]) {
                                          const updated = { ...next[resp.id] };
                                          delete updated[valIdx];
                                          next[resp.id] = updated;
                                        }
                                        return next;
                                      })
                                    }
                                    disabled={inlineSavingKey === `${resp.id}-simple-${valIdx}`}
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setInlineSimpleEdits((prev) => ({
                                      ...(prev || {}),
                                      [resp.id]: { ...(prev?.[resp.id] || {}), [valIdx]: val },
                                    }));
                                    setInlineEditError(null);
                                    setInlineEditFeedback(null);
                                  }}
                                  disabled={!canEditSplits}
                                >
                                  Edit
                                </Button>
                                {canSplit && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                      setSimpleSplitModal({
                                        open: true,
                                        responseId: resp.id,
                                        raw: val,
                                        names,
                                      })
                                    }
                                  >
                                    Suggest Split
                                  </Button>
                                )}
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : Array.isArray(nominationsArray) && nominationsArray.length > 0 ? (
                  nominationsArray.map((entry: any, entryIdx: number) => {
                    const fieldKeys = [
                      ...panelFieldOrder,
                      ...Object.keys(entry || {}).filter(
                        (key) => !panelFieldOrder.includes(key) && !/comment/i.test(key)
                      ),
                    ];
                    const orgVals = panelOrgField ? getAnswerVals(entry, panelOrgField) : [];
                    const entryTitle = orgVals[0] || `Nomination ${entryIdx + 1}`;
                    const splitSuggestion = buildSplitSuggestion(entry, panelOrgField);
                    const entryEdits = inlineEntryEdits[resp.id]?.[entryIdx];
                    const isEditingEntry = Boolean(entryEdits);

                    return (
                      <Box
                        key={`${resp.id}-entry-${entryIdx}`}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                          p: 1.5,
                          backgroundColor: "#fafbfc",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {entryTitle}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Tooltip
                              title={
                                canEditSplits
                                  ? ""
                                  : "You do not have permission to save splits. Only owners/editors can edit responses."
                              }
                            >
                              <span>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disabled={!canEditSplits || isEditingEntry}
                                  onClick={() => {
                                    const initial: Record<string, string[]> = {};
                                    fieldKeys.forEach((key) => {
                                      const vals = getAnswerVals(entry, key);
                                      if (vals.length && vals.every((v) => typeof v === "string")) {
                                        initial[key] = vals;
                                      } else if (typeof (entry as any)?.[key] === "string") {
                                        initial[key] = [(entry as any)?.[key] as string];
                                      }
                                    });
                                    setInlineEntryEdits((prev) => ({
                                      ...(prev || {}),
                                      [resp.id]: { ...(prev?.[resp.id] || {}), [entryIdx]: initial },
                                    }));
                                    setInlineEditError(null);
                                    setInlineEditFeedback(null);
                                  }}
                                >
                                  Edit fields
                                </Button>
                              </span>
                            </Tooltip>
                            {splitSuggestion && (
                              <Tooltip
                                title={
                                  canEditSplits
                                    ? ""
                                    : "You do not have permission to save splits. Only owners/editors can edit responses."
                                }
                              >
                                <span>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={!canEditSplits}
                                    onClick={() =>
                                      setSplitModal({
                                        open: true,
                                        suggestion: splitSuggestion,
                                        title: entryTitle,
                                        responseId: resp.id,
                                        entryIndex: entryIdx,
                                      })
                                    }
                                  >
                                    Suggest Split
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>
                        <Stack spacing={1}>
                          {fieldKeys.map((key) => {
                            if (!key) return null;
                            const label = panelFieldLabels[key] || friendlyLabel(key);
                            const values = getAnswerVals(entry, key);
                            const editableVals = entryEdits?.[key];
                            const canEditField = editableVals !== undefined;
                            return (
                              <Box key={key}>
                                <Typography variant="caption" color="text.secondary">
                                  {label}
                                </Typography>
                                {canEditField ? (
                                  <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                                    {(editableVals || []).map((val, i) => (
                                      <Box key={`${key}-${i}`} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          value={val}
                                          onChange={(e) => {
                                            const nextVal = e.target.value;
                                            setInlineEntryEdits((prev) => {
                                              const next = { ...(prev || {}) };
                                              const respMap = { ...(next[resp.id] || {}) };
                                              const entryMap = { ...(respMap[entryIdx] || {}) };
                                              const fieldVals = [...(entryMap[key] || [])];
                                              fieldVals[i] = nextVal;
                                              entryMap[key] = fieldVals;
                                              respMap[entryIdx] = entryMap;
                                              next[resp.id] = respMap;
                                              return next;
                                            });
                                            setInlineEditError(null);
                                            setInlineEditFeedback(null);
                                          }}
                                          disabled={inlineSavingKey === `${resp.id}-entry-${entryIdx}`}
                                        />
                                        <IconButton
                                          aria-label="Remove value"
                                          size="small"
                                          onClick={() => {
                                            setInlineEntryEdits((prev) => {
                                              const next = { ...(prev || {}) };
                                              const respMap = { ...(next[resp.id] || {}) };
                                              const entryMap = { ...(respMap[entryIdx] || {}) };
                                              const fieldVals = [...(entryMap[key] || [])].filter((_, idx) => idx !== i);
                                              entryMap[key] = fieldVals;
                                              respMap[entryIdx] = entryMap;
                                              next[resp.id] = respMap;
                                              return next;
                                            });
                                            setInlineEditError(null);
                                            setInlineEditFeedback(null);
                                          }}
                                          disabled={inlineSavingKey === `${resp.id}-entry-${entryIdx}`}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    ))}
                                    <Button
                                      startIcon={<AddCircleOutlineIcon />}
                                      size="small"
                                      onClick={() => {
                                        setInlineEntryEdits((prev) => {
                                          const next = { ...(prev || {}) };
                                          const respMap = { ...(next[resp.id] || {}) };
                                          const entryMap = { ...(respMap[entryIdx] || {}) };
                                          const fieldVals = [...(entryMap[key] || []), ""];
                                          entryMap[key] = fieldVals;
                                          respMap[entryIdx] = entryMap;
                                          next[resp.id] = respMap;
                                          return next;
                                        });
                                        setInlineEditError(null);
                                        setInlineEditFeedback(null);
                                      }}
                                      disabled={inlineSavingKey === `${resp.id}-entry-${entryIdx}`}
                                      sx={{ alignSelf: "flex-start" }}
                                    >
                                      Add value
                                    </Button>
                                  </Stack>
                                ) : (
                                  <ValueChips values={values} />
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                        {isEditingEntry && (
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSaveEntryEdit(resp.id, entryIdx)}
                              disabled={!canEditSplits || inlineSavingKey === `${resp.id}-entry-${entryIdx}`}
                              startIcon={
                                inlineSavingKey === `${resp.id}-entry-${entryIdx}` ? (
                                  <CircularProgress size={14} />
                                ) : undefined
                              }
                            >
                              Save changes
                            </Button>
                            <Button
                              variant="text"
                              size="small"
                              onClick={() =>
                                setInlineEntryEdits((prev) => {
                                  const next = { ...(prev || {}) };
                                  if (next[resp.id]) {
                                    const updated = { ...next[resp.id] };
                                    delete updated[entryIdx];
                                    next[resp.id] = updated;
                                  }
                                  return next;
                                })
                              }
                              disabled={inlineSavingKey === `${resp.id}-entry-${entryIdx}`}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        )}
                      </Box>
                    );
                  })
                ) : targetValsSimple.length > 0 ? (
                  <Stack spacing={1}>
                    {targetValsSimple.map((val, valIdx) => {
                      const names = splitEntities(val);
                      const canSplit = names.length > 1 && canEditSplits;
                      const canEditDirect = typeof resp.answers?.[target] === "string";
                      const editingVal = inlineTargetEdits[resp.id];

                      return (
                        <Box key={`${resp.id}-fallback-${valIdx}`} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                          <Stack spacing={0.75}>
                            <ValueChips values={[val]} />
                            {editingVal !== undefined ? (
                              <Stack spacing={1}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={editingVal}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setInlineTargetEdits((prev) => ({ ...(prev || {}), [resp.id]: value }));
                                    setInlineEditError(null);
                                    setInlineEditFeedback(null);
                                  }}
                                  disabled={inlineSavingKey === `${resp.id}-target-string`}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleSaveTargetStringEdit(resp.id)}
                                    disabled={!canEditSplits || inlineSavingKey === `${resp.id}-target-string`}
                                    startIcon={
                                      inlineSavingKey === `${resp.id}-target-string` ? (
                                        <CircularProgress size={14} />
                                      ) : undefined
                                    }
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={() =>
                                      setInlineTargetEdits((prev) => {
                                        const next = { ...(prev || {}) };
                                        delete next[resp.id];
                                        return next;
                                      })
                                    }
                                    disabled={inlineSavingKey === `${resp.id}-target-string`}
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                {canEditDirect && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                      const rawVal = typeof resp.answers?.[target] === "string" ? resp.answers[target] : val;
                                      setInlineTargetEdits((prev) => ({ ...(prev || {}), [resp.id]: rawVal }));
                                      setInlineEditError(null);
                                      setInlineEditFeedback(null);
                                    }}
                                    disabled={!canEditSplits}
                                  >
                                    Edit
                                  </Button>
                                )}
                                {canSplit && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                      setSimpleSplitModal({
                                        open: true,
                                        responseId: resp.id,
                                        raw: val,
                                        names,
                                      })
                                    }
                                  >
                                    Suggest Split
                                  </Button>
                                )}
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    No nominations recorded.
                  </Typography>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <Dialog
        open={simpleSplitModal.open}
        onClose={() => {
          setSimpleSplitModal({ open: false });
          setEditableSimpleNames([]);
          setSplitError(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Suggest split</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Original text
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {simpleSplitModal.raw}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Proposed entities (editable)
              </Typography>
              <Stack spacing={1}>
                {editableSimpleNames.map((name, idx) => (
                  <TextField
                    key={idx}
                    fullWidth
                    size="small"
                    value={name}
                    onChange={(e) => {
                      const next = [...editableSimpleNames];
                      next[idx] = e.target.value;
                      setEditableSimpleNames(next);
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSimpleSplitModal({ open: false });
              setEditableSimpleNames([]);
              setSplitError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplySimpleSplit}
            disabled={savingSplit || !simpleSplitModal.responseId}
          >
            Save split
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={splitModal.open}
        onClose={() => {
          setSplitModal({ open: false });
          setCopyFeedback(null);
          setEditableNames([]);
          setShowJson(false);
          setSplitError(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Suggested collaborator split</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Entry
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {splitModal.title || "Collaborator"}
              </Typography>
            </Box>
            {splitModal.suggestion && (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Original text
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {splitModal.suggestion.raw}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Proposed entities (editable)
                  </Typography>
                  <Stack spacing={1}>
                    {editableNames.map((name, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          value={name}
                          disabled={!canEditSplits}
                          onChange={(e) => {
                            const next = [...editableNames];
                            next[idx] = e.target.value;
                            setEditableNames(next);
                            setCopyFeedback(null);
                          }}
                        />
                        <IconButton
                          aria-label="Remove proposed entity"
                          disabled={!canEditSplits}
                          onClick={() => {
                            const next = editableNames.filter((_, i) => i !== idx);
                            setEditableNames(next);
                            setCopyFeedback(null);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddCircleOutlineIcon />}
                      size="small"
                      onClick={() => {
                        setEditableNames((prev) => [...prev, ""]);
                        setCopyFeedback(null);
                      }}
                      disabled={!canEditSplits}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      Add proposed entity
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Edit the split names here; other fields remain unchanged. Data is only saved when you click "Save split to database."
                    </Typography>
                  </Stack>
                </Box>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowJson((v) => !v)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {showJson ? "Hide JSON" : "Show JSON"}
                </Button>
                <Collapse in={showJson}>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Original node JSON
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          backgroundColor: "#f5f5f5",
                          p: 1.5,
                          borderRadius: 1,
                          overflowX: "auto",
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {JSON.stringify(splitModal.suggestion?.original ?? {}, null, 2)}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Proposed nodes JSON (ready to paste)
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          backgroundColor: "#f5f5f5",
                          p: 1.5,
                          borderRadius: 1,
                          overflowX: "auto",
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {JSON.stringify(proposedPayload ?? {}, null, 2)}
                      </Box>
                    </Box>
                  </Stack>
                </Collapse>
                {splitError && (
                  <Typography variant="caption" color="error">
                    {splitError}
                  </Typography>
                )}
                {!canEditSplits && (
                  <Typography variant="caption" color="text.secondary">
                    You can view splits but cannot save changes with your current role.
                  </Typography>
                )}
                {copyFeedback && (
                  <Typography variant="caption" color="success.main">
                    {copyFeedback}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleApplySplit}
            variant="contained"
            disabled={savingSplit || !splitModal.suggestion || !proposedPayload || !canEditSplits}
            startIcon={savingSplit ? <CircularProgress size={16} /> : undefined}
          >
            {savingSplit ? "Saving…" : "Save split to database"}
          </Button>
          <Button
            onClick={async () => {
              if (!splitModal.suggestion) return;
              const ok = await copyText(JSON.stringify(proposedPayload ?? {}, null, 2));
              setCopyFeedback(ok ? "Copied JSON to clipboard" : "Copy failed");
            }}
            variant="outlined"
          >
            Copy JSON
          </Button>
          <Button
            onClick={async () => {
              if (!splitModal.suggestion) return;
              const ok = await copyText(trimmedNames.join("\n"));
              setCopyFeedback(ok ? "Copied text to clipboard" : "Copy failed");
            }}
            variant="outlined"
          >
            Copy text
          </Button>
          <Button
            onClick={() => {
              setSplitModal({ open: false });
              setCopyFeedback(null);
              setEditableNames([]);
              setShowJson(false);
              setSplitError(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
